import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import { JobApplication } from "../../../Entities/job-application.entity";
import { InterviewSession } from "../../../Interface/Index";
import LoaderContext from "../../../Context/LoaderContext/Index";
import AgentService from "../../../Services/agent/agent.service";
import { DashboardContext } from "../../../Context/DashboardContext";
import { useInterviewRoundContext } from "../../../Context/InterviewRoundContext";
import messages from "../../../Services/agent/messages";
import aiInterviewService from "../../../Services/ai-interview.service";
import usePollingFunctions from "./usePollingFunctions";
import useInterviewCommonHandlers from "./useInterviewCommonHandlers";

interface InterviewEvaluationFlowReturnType {
  handleEvaluation: () => Promise<void>;
  handleRetryEvaluation: () => Promise<void>;
  handleUnqualifiedRound: (
    appliedJob: JobApplication,
    session: InterviewSession
  ) => Promise<void>;
  handleSessionRecovery: (
    sessionId: string,
    session: InterviewSession
  ) => Promise<void>;
}

const useInterviewEvaluationFlow = (): InterviewEvaluationFlowReturnType => {
  const navigate = useNavigate();
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);
  const agentService = AgentService.getInstance();

  const {
    appliedJob,
    checkLastRound,
    interviewSessions,
    enableRounds,
    enabledInterviewRoundTabs,
    addSession,
    addSessionAndEnableRound,
    jobDescription,
    setAppliedJob,
    setShowResultTab,
    setShowOnlineInterviewTab,
    updateSession,
    setIsProcessingNextRound,
  } = useContext(DashboardContext);

  const {
    setActiveQuestionIndex,
    currentSessionQuestions,
    isRoundFinished,
    currentSession,
    setShowRetryEvaluation,
    setCurrentSession,
    setShowCamRecorder,
    setIsRoundFinished,
    setShowCameraConfirmation,
    setShowExitConfirmation,
    setExitTimer,
    setIsEvaluating,
    setIsFullscreen,
    setIsVisibilityActive,
  } = useInterviewRoundContext();

  const { toggleToNormalScreenMode } = useInterviewCommonHandlers();

  // Polling functions
  const {
    checkForNewSessions,
    checkForNextRoundQuestions,
    checkApplicationStatus,
    checkEvaluationCompletedOrNot,
  } = usePollingFunctions();

  const handleClearAllStates = () => {
    setIsRoundFinished(false);
    setShowCamRecorder(false);
    setShowCameraConfirmation(false);
    setShowExitConfirmation(false);
    setExitTimer(false);
    setIsFullscreen(false);
    setIsVisibilityActive(true);
  };

  const handleQualifiedRound = async (
    appliedJob: JobApplication,
    currentRound: number
  ) => {
    try {
      setIsProcessingNextRound(true);
      setLoadingMessage("Creating next round, please wait...");
      agentService.setCurrentMessage(messages.PREPARING_NEXT_ROUND, "", "");

      const newSessions = await checkForNewSessions(
        appliedJob?._id,
        interviewSessions,
        currentRound
      );

      if (!newSessions || newSessions.length === 0) {
        console.error("No new interview sessions found.");
        setLoading(false);
        setLoadingMessage(null);
        setIsProcessingNextRound(false);
        return;
      }

      setLoadingMessage("Generating questionnaire...");
      // agentService.setCurrentMessage(messages.GENERATING_QUESTIONNAIRE, "", ""); // REMOVE this line to avoid Oops message

      const nextRoundSession = newSessions.find(
        (session) => session.status === "pending"
      );

      if (!nextRoundSession) {
        console.error("No pending session found.");
        setLoading(false);
        setLoadingMessage(null);
        setIsProcessingNextRound(false);
        return;
      }

      updateSession(nextRoundSession._id, nextRoundSession);

      setLoadingMessage("Generating questions, please wait...");
      // agentService.setCurrentMessage(messages.GENERATING_QUESTIONNAIRE, "", "");
      const newSession = await checkForNextRoundQuestions(nextRoundSession._id);

      if (newSession?.isQuestionsGenerated) {
        const roundName = enabledInterviewRoundTabs.find(
          (round) => round.roundId === nextRoundSession.interviewRoundId
        )?.roundName;

        if (roundName) {
          addSessionAndEnableRound(nextRoundSession);
          setCurrentSession(nextRoundSession);
          setLoading(false);
          setLoadingMessage(null);
          setActiveQuestionIndex(-1);
          // Update agent message to reflect new round only after session is ready
          // agentService.setAgentMessageBasedOnJobApplication(
          //   appliedJob,
          //   [...interviewSessions, nextRoundSession],
          //   enabledInterviewRoundTabs,
          //   appliedJob?._id
          // );
          navigate(
            `/${jobDescription?._id}/interview-round/${nextRoundSession._id}`
          );
          setIsProcessingNextRound(false);
        }
      } else {
        console.warn("No questions found for the next round.");
        setIsProcessingNextRound(false);
      }
    } catch (error) {
      console.error("Error handling qualified round:", error);
      setIsProcessingNextRound(false);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
      localStorage.removeItem("refresh");
      localStorage.removeItem("refreshedSessionId");
    }
  };

  const handleUnqualifiedRound = async (
    appliedJob: JobApplication,
    session: InterviewSession
  ) => {
    agentService.setCurrentMessage(messages.EVALUATION_UNQUALIFIED, "", "");
    session.status = "unqualified";
    setCurrentSession(session);
    setIsRoundFinished(true);
    setActiveQuestionIndex(currentSessionQuestions.length);
    let updatedApplication = await checkApplicationStatus(
      appliedJob._id,
      "rejected"
    );
    if (!updatedApplication) {
      console.error("No updated application found");
      return;
    }
    setAppliedJob(updatedApplication);
    setShowResultTab(true);
    setLoading(false);
    setLoadingMessage(null);
    localStorage.removeItem("refresh");
    localStorage.removeItem("refreshedSessionId");
    navigate(`/${jobDescription?._id}/result`);
  };

  const handleLastRoundQualified = async (
    appliedJob: JobApplication,
    session: InterviewSession
  ) => {
    if (!jobDescription) {
      console.error("No job description found");
      return;
    }
    try {
      const isOnlineInterviewEnabled = jobDescription.onlineInterview;
      if (isOnlineInterviewEnabled) {
        setLoadingMessage("Setting up online interview...");
        agentService.setCurrentMessage(messages.EVALUATION_QUALIFIED, "", "");
        const updatedApplication = await checkApplicationStatus(
          appliedJob._id,
          "interview"
        );
        if (!updatedApplication) {
          console.error("No updated application found");
          return;
        }
        setAppliedJob(updatedApplication);
        setIsRoundFinished(true);
        setShowOnlineInterviewTab(true);
        agentService.setCurrentMessage(
          messages.WAITING_FOR_INTERVIEW_SCHEDULE,
          "",
          "Select Slot"
        );
        navigate(`/${jobDescription?._id}/online-interview`);
      } else {
        const updatedApplication = await checkApplicationStatus(
          appliedJob._id,
          "shortlisted"
        );
        if (!updatedApplication) {
          console.error("No updated application found");
          return;
        }
        setAppliedJob(updatedApplication);
        session.status = "qualified";
        setCurrentSession(session);
        setIsRoundFinished(true);
        setShowResultTab(true);
        agentService.setCurrentMessage(messages.ALL_ROUND_CLEARED, "", "");
        localStorage.removeItem("refresh");
        localStorage.removeItem("refreshedSessionId");
        setLoading(false);
        setLoadingMessage(null);
        navigate(`/${jobDescription?._id}/result`);
      }
    } catch (error) {
      console.error("Error handling last round qualified:", error);
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleRetryEvaluation = async () => {
    setLoading(true);
    setShowRetryEvaluation(false);
    try {
      return await handleEvaluation();
    } catch (error) {
      console.error("Error during retry evaluation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionRecovery = async (
    sessionId: string,
    session: InterviewSession
  ) => {
    console.log("Recovery initiated for session:", sessionId);
    try {
      if (!session) {
        console.log("handleSessionRecovery: No session found");
        return;
      }

      if (!appliedJob) {
        console.log("handleSessionRecovery: No applied job found");
        return;
      }

      if (session._id !== sessionId) {
        console.log("handleSessionRecovery: Session ID mismatch");
        return;
      }

      setCurrentSession(session);

      const status = session.status;

      if (status === "pending") {
        const updatedSession = await checkEvaluationCompletedOrNot(sessionId);
        if (!updatedSession) {
          console.error("No updated session found");
          return;
        }
        setCurrentSession(updatedSession);

        if (updatedSession.status === "unqualified") {
          await handleUnqualifiedRound(appliedJob, updatedSession);
          // Only update agent message after session is fully handled
          agentService.setAgentMessageBasedOnJobApplication(
            appliedJob,
            interviewSessions,
            enabledInterviewRoundTabs,
            appliedJob?._id
          );
          return;
        } else if (updatedSession.status === "qualified") {
          const isLastRound = checkLastRound(updatedSession._id);
          if (isLastRound) {
            await handleLastRoundQualified(appliedJob, updatedSession);
            // Only update agent message after session is fully handled
            agentService.setAgentMessageBasedOnJobApplication(
              appliedJob,
              interviewSessions,
              enabledInterviewRoundTabs,
              appliedJob?._id
            );
            return;
          }
          await handleQualifiedRound(
            appliedJob,
            updatedSession.interviewRoundNumber
          );
          // Only update agent message after session is fully handled
          agentService.setAgentMessageBasedOnJobApplication(
            appliedJob,
            interviewSessions,
            enabledInterviewRoundTabs,
            appliedJob?._id
          );
          return;
        }
      }

      if (status === "qualified") {
        const isLastRound = checkLastRound(sessionId);
        if (isLastRound) {
          await handleLastRoundQualified(appliedJob, session);
          agentService.setAgentMessageBasedOnJobApplication(
            appliedJob,
            interviewSessions,
            enabledInterviewRoundTabs,
            appliedJob?._id
          );
          return;
        }
        await handleQualifiedRound(appliedJob, session.interviewRoundNumber);
        agentService.setAgentMessageBasedOnJobApplication(
          appliedJob,
          interviewSessions,
          enabledInterviewRoundTabs,
          appliedJob?._id
        );
        return;
      }

      if (status === "unqualified") {
        await handleUnqualifiedRound(appliedJob, session);
        agentService.setAgentMessageBasedOnJobApplication(
          appliedJob,
          interviewSessions,
          enabledInterviewRoundTabs,
          appliedJob?._id
        );
        return;
      }
    } catch (error: any) {
      console.error("Error during session recovery:", error);
      return;
    }
  };

  const getBrowserFingerPrint = async () => {
    const { hash } = await getFingerprint(true);
    return hash;
  };

  const handleEvaluation = async () => {
    if (!currentSession || !appliedJob) {
      console.log("handleEvaluation: No session found");
      return;
    }

    if (isRoundFinished) {
      console.log("handleEvaluation: Round is already finished");
      return;
    }

    setLoading(true);
    setLoadingMessage("Evaluating your answers please wait...");
    setIsEvaluating(true);
    toggleToNormalScreenMode();

    try {
      agentService.setCurrentMessage(
        messages.WAITING_FOR_EVALUATION_RESULT,
        "",
        ""
      );
      const browserFingerprint = await getBrowserFingerPrint();

      const evaluationResult = await aiInterviewService.evaluateInterview(
        currentSession._id,
        browserFingerprint
      );

      if (!evaluationResult) {
        console.error("handleEvaluation: No evaluation result found");
        setLoading(false);
        setLoadingMessage(null);
        setShowRetryEvaluation(true);
        return;
      }

      if (!evaluationResult.success) {
        console.error("handleEvaluation: workflow failed");
        setLoading(false);
        setLoadingMessage(null);
        setShowRetryEvaluation(true);
        return;
      }

      const result = evaluationResult.status;
      if (!result) {
        console.error("handleEvaluation: Failed to finish the exam");
        setLoading(false);
        setLoadingMessage(null);
        setShowRetryEvaluation(true);
        setIsEvaluating(false);
        return;
      }

      setIsEvaluating(false);

      // Handling evaluation status
      if (result === "qualified") {
        currentSession.status = "qualified";
        setCurrentSession(currentSession);
        updateSession(currentSession._id, { ...currentSession }); // <-- update context array
        const isLastRound = checkLastRound(currentSession._id);
        handleClearAllStates();
        setIsRoundFinished(true);
        if (isLastRound) {
          // Update agent message for last round
          agentService.setAgentMessageBasedOnJobApplication(
            appliedJob,
            interviewSessions.map(s => s._id === currentSession._id ? { ...s, status: "qualified" } : s),
            enabledInterviewRoundTabs,
            appliedJob?._id
          );
          return await handleLastRoundQualified(appliedJob, currentSession);
        }
        return await handleQualifiedRound(
          appliedJob,
          currentSession.interviewRoundNumber
        );
      } else if (result === "unqualified") {
        return await handleUnqualifiedRound(appliedJob, currentSession);
      }
    } catch (error) {
      console.error("Failed to finish the exam", error);
      setTimeout(() => {
        setLoading(false);
        setLoadingMessage(null);
      }, 1000);
      setShowRetryEvaluation(true);
      setIsEvaluating(false);
    }
  };

  return {
    handleEvaluation,
    handleRetryEvaluation,
    handleUnqualifiedRound,
    handleSessionRecovery,
  };
};

export default useInterviewEvaluationFlow;
